/**
 * Einheitliche Kassierer-Berechtigungen (Routen, Navigation, Settings, Druck/Export).
 */

export const WORKER_PAGE_PERMISSION_KEYS = [
  'dashboard',
  'sales',
  /** Nicht-Seite: Storno (Voll-/Zeilen). Default aus — nur explizit oder Manager. */
  'voidSales',
  'inventory',
  'stock',
  /** Nicht-Seite: Schreibrechte auf /stock (Transfer, Lot-Edit, Emplacements). */
  'stockWrite',
  'purchases',
  'orders',
  'customers',
  'suppliers',
  'payments',
  'expenses',
  'reports',
  'settings'
];

/** In UI zuweisbare Rechte (settings nur für Manager). */
export const WORKER_ASSIGNABLE_PERMISSION_KEYS = WORKER_PAGE_PERMISSION_KEYS.filter(
  (key) => key !== 'settings'
);

/**
 * Seitliche Module (Navigation / ProtectedRoute).
 * voidSales + stockWrite sind Fähigkeiten, keine eigenen Routen.
 */
export const WORKER_MODULE_PERMISSION_KEYS = WORKER_ASSIGNABLE_PERMISSION_KEYS.filter(
  (key) => key !== 'voidSales' && key !== 'stockWrite'
);

/** Fähigkeiten, die an ein Modul gekoppelt sind. */
export const WORKER_CAPABILITY_PERMISSION_KEYS = ['voidSales', 'stockWrite'];

export const DEFAULT_WORKER_PERMISSIONS = {
  dashboard: true,
  sales: true,
  voidSales: false,
  inventory: true,
  stock: true,
  stockWrite: true,
  purchases: false,
  orders: false,
  customers: false,
  suppliers: false,
  payments: true,
  expenses: true,
  reports: false,
  settings: false
};

/** @deprecated Legacy-Key — wird in normalizeWorkerPermissions nach expenses gemappt. */
export const LEGACY_FEED_EXPENSES_KEY = 'feedExpenses';

/**
 * Strikt boolean — JSON/Cloud liefern oft "true"/"false" Strings.
 * Boolean("false") === true war eine echte Sicherheitslücke (Storno, Module).
 * @param {unknown} value
 * @returns {boolean|undefined} undefined = Schlüssel fehlt / unklar → Defaults greifen
 */
export function coercePermissionFlag(value) {
  if (value === undefined) return undefined;
  if (value === null) return false;
  if (value === true || value === 1) return true;
  if (value === false || value === 0) return false;
  if (typeof value === 'string') {
    const s = value.trim().toLowerCase();
    if (s === 'true' || s === '1' || s === 'yes' || s === 'on') return true;
    if (s === 'false' || s === '0' || s === 'no' || s === 'off' || s === '') return false;
  }
  return Boolean(value);
}

/**
 * Kanonisches Default-Set (kopie) für Create-Formular & Neu-Kassierer.
 * @returns {Record<string, boolean>}
 */
export function createDefaultWorkerPermissions() {
  return normalizeWorkerPermissions({ ...DEFAULT_WORKER_PERMISSIONS });
}

/**
 * @param {Record<string, boolean>|null|undefined} permissions
 * @param {{ mode?: 'seed'|'enforce' }=} options
 *   - seed (default): fehlende Keys → DEFAULT (Create, Migration, Persist)
 *   - enforce: fehlende Keys → false (Guards / hasPagePermission) — deny-by-default
 * @returns {Record<string, boolean>}
 */
export function normalizeWorkerPermissions(permissions = {}, options = {}) {
  const source = permissions && typeof permissions === 'object' ? permissions : {};
  const enforce = options?.mode === 'enforce';
  const normalized = {};

  for (const key of WORKER_PAGE_PERMISSION_KEYS) {
    const coerced = coercePermissionFlag(source[key]);
    if (coerced === undefined) {
      // Seed: migrate / create. Enforce: sparse session must not re-open defaults.
      normalized[key] = enforce ? false : Boolean(DEFAULT_WORKER_PERMISSIONS[key]);
    } else {
      normalized[key] = coerced;
    }
  }

  // Legacy feedExpenses → expenses (nur wenn expenses nicht explizit gesetzt)
  if (
    coercePermissionFlag(source.feedExpenses) === true &&
    coercePermissionFlag(source.expenses) === undefined
  ) {
    normalized.expenses = true;
  }

  // Writing stock without seeing it is not a state the UI can represent.
  if (!normalized.stock) {
    normalized.stockWrite = false;
  }
  // Storno ohne Ventes-Modul ergibt keinen Sinn.
  if (!normalized.sales) {
    normalized.voidSales = false;
  }
  // Paramètres is manager-only — never grant via stored worker permissions.
  normalized.settings = false;
  return normalized;
}

/** Session/Guard-Pfad: fehlende Rechte = verweigert (kein DEFAULT-Auffüllen). */
export function normalizeWorkerPermissionsForEnforce(permissions) {
  return normalizeWorkerPermissions(permissions, { mode: 'enforce' });
}

/**
 * Effektiv-Zustand: gespeicherte Rechte + Pending-Overlay (Create oder Edit).
 * @param {Record<string, boolean>|null|undefined} baseline
 * @param {Record<string, boolean>|null|undefined} pending
 */
export function resolveEffectiveWorkerPermissions(baseline, pending) {
  return normalizeWorkerPermissions({
    ...normalizeWorkerPermissions(baseline || {}),
    ...(pending && typeof pending === 'object' ? pending : {}),
  });
}

/**
 * Pending-Map key (id may be string or number).
 * @param {string|number} workerId
 * @param {Record<string, object>} [pendingMap]
 */
export function pendingMapKey(workerId, pendingMap = {}) {
  if (pendingMap[workerId] !== undefined) return workerId;
  if (pendingMap[String(workerId)] !== undefined) return String(workerId);
  return String(workerId);
}

/** @param {Record<string, object>} pendingMap @param {string|number} workerId */
export function clearPendingForWorker(pendingMap, workerId) {
  const next = { ...pendingMap };
  delete next[workerId];
  delete next[String(workerId)];
  return next;
}

/**
 * Toggle one key in a draft/partial permission overlay (respects stock/void deps).
 * @param {Record<string, boolean>|null|undefined} baseline
 * @param {Record<string, boolean>|null|undefined} previousPending
 * @param {string} permission
 */
export function togglePendingPermission(baseline, previousPending, permission) {
  const base = normalizeWorkerPermissions(baseline || {});
  const prev = previousPending && typeof previousPending === 'object' ? previousPending : {};
  const current = Object.prototype.hasOwnProperty.call(prev, permission)
    ? Boolean(prev[permission])
    : Boolean(base[permission]);
  const stockOn = prev.stock !== undefined ? Boolean(prev.stock) : Boolean(base.stock);
  const salesOn = prev.sales !== undefined ? Boolean(prev.sales) : Boolean(base.sales);
  const next = { ...prev, [permission]: !current };
  if (permission === 'stock' && !next.stock) next.stockWrite = false;
  if (permission === 'sales' && !next.sales) next.voidSales = false;
  if (permission === 'stockWrite' && !stockOn) next.stockWrite = false;
  if (permission === 'voidSales' && !salesOn) next.voidSales = false;
  return next;
}

/** Toggle one flag on a full permission object (create form). */
export function togglePermissionFlag(perms, key) {
  const base = normalizeWorkerPermissions(perms || {});
  return normalizeWorkerPermissions({ ...base, [key]: !base[key] });
}

/** @param {Record<string, boolean>|null|undefined} permissions */
export function countActiveModules(permissions) {
  const perms = normalizeWorkerPermissions(permissions || {});
  return {
    active: WORKER_MODULE_PERMISSION_KEYS.filter((k) => perms[k]).length,
    total: WORKER_MODULE_PERMISSION_KEYS.length,
  };
}

/**
 * Pending hat mindestens einen von Baseline abweichenden Wert.
 */
export function hasWorkerPermissionPending(baseline, pending) {
  if (!pending || typeof pending !== 'object') return false;
  if (Object.keys(pending).length === 0) return false;
  const base = normalizeWorkerPermissions(baseline || {});
  const effective = resolveEffectiveWorkerPermissions(baseline, pending);
  return WORKER_ASSIGNABLE_PERMISSION_KEYS.some((key) => effective[key] !== base[key]);
}

/**
 * Mindestens ein nutzbares Modul für Login/Nav (sonst toter Kassierer-Account).
 * @param {Record<string, boolean>|null|undefined} permissions
 */
export function workerHasAtLeastOneModule(permissions) {
  const perms = normalizeWorkerPermissions(permissions || {});
  return WORKER_MODULE_PERMISSION_KEYS.some((key) => perms[key]);
}

/**
 * Bulk-Zuweisung: Module ja/nein. Storno (voidSales) wird nie per „Tout“ vergeben.
 * stockWrite folgt stock bei bulk-an; bulk-aus schaltet alles ab.
 * @param {boolean} value
 * @returns {Record<string, boolean>}
 */
export function buildBulkWorkerPermissions(value) {
  const next = {};
  for (const key of WORKER_MODULE_PERMISSION_KEYS) {
    next[key] = Boolean(value);
  }
  next.voidSales = false;
  next.stockWrite = Boolean(value) && Boolean(next.stock);
  next.settings = false;
  return normalizeWorkerPermissions(next);
}

/**
 * Lese-/Schreibrechte für /stock. Ohne `stockWrite` bleibt die Seite read-only —
 * Transfer, Lot-Bearbeitung, Produkt-Edit und Emplacement-Verwaltung sind gesperrt.
 * @param {{ type?: string, permissions?: Record<string, boolean> }|null|undefined} currentRole
 */
export function resolveStockCapabilities(currentRole) {
  if (!currentRole) return { canRead: false, canWrite: false };
  if (currentRole.type === 'manager' || currentRole.type === 'admin') {
    return { canRead: true, canWrite: true };
  }
  if (currentRole.type === 'worker') {
    const perms = normalizeWorkerPermissionsForEnforce(currentRole.permissions);
    return { canRead: Boolean(perms.stock), canWrite: Boolean(perms.stockWrite) };
  }
  return { canRead: false, canWrite: false };
}

/**
 * Storno (Voll-/Zeilen). Manager/Admin immer; Worker nur mit explizitem `voidSales`.
 * @param {{ type?: string, permissions?: Record<string, boolean> }|null|undefined} currentRole
 */
export function canVoidSales(currentRole) {
  if (!currentRole) return false;
  if (currentRole.type === 'manager' || currentRole.type === 'admin') return true;
  if (currentRole.type === 'worker') {
    return Boolean(normalizeWorkerPermissionsForEnforce(currentRole.permissions).voidSales);
  }
  return false;
}

/**
 * @param {{ type?: string, permissions?: Record<string, boolean> }|null|undefined} currentRole
 * @param {string} permission
 */
export function hasPagePermission(currentRole, permission) {
  if (!currentRole || !permission) return false;
  if (currentRole.type === 'manager' || currentRole.type === 'admin') return true;
  if (currentRole.type === 'worker') {
    // Defense in depth: Paramètres never reachable for caissiers.
    if (permission === 'settings') return false;
    const perms = normalizeWorkerPermissionsForEnforce(currentRole.permissions);
    return Boolean(perms[permission]);
  }
  return false;
}

/**
 * Druck/Export an Seitenrechten gekoppelt (Manager/Admin: alles).
 *
 * `canPrintReports` bleibt strikt an `reports` gebunden — Finanzberichte (Cash-Report,
 * offene Lieferantenrechnungen) dürfen nicht über die per Default aktiven Rechte
 * `inventory`/`stock` erreichbar sein. Bestandslisten laufen über `canPrintInventory`.
 *
 * @param {{ type?: string, permissions?: Record<string, boolean> }|null|undefined} currentRole
 */
export function resolvePrintExportCapabilities(currentRole) {
  if (!currentRole) {
    return {
      canPrint: false,
      canExport: false,
      canPrintReports: false,
      canPrintInventory: false
    };
  }
  if (currentRole.type === 'manager' || currentRole.type === 'admin') {
    return {
      canPrint: true,
      canExport: true,
      canPrintReports: true,
      canPrintInventory: true
    };
  }
  if (currentRole.type === 'worker') {
    const perms = normalizeWorkerPermissionsForEnforce(currentRole.permissions);
    const canPrintInventory = Boolean(perms.inventory || perms.stock);
    return {
      canPrint: Boolean(perms.sales || perms.payments || perms.dashboard),
      canExport: Boolean(perms.reports),
      canPrintReports: Boolean(perms.reports),
      canPrintInventory
    };
  }
  return {
    canPrint: false,
    canExport: false,
    canPrintReports: false,
    canPrintInventory: false
  };
}

/**
 * @param {{ type?: string, permissions?: Record<string, boolean> }|null|undefined} currentRole
 * @param {'fr'|'ar'|string} [lang]
 */
export function assertCanPrintReceipt(currentRole, lang = 'fr') {
  if (resolvePrintExportCapabilities(currentRole).canPrint) return;
  const message =
    lang === 'ar'
      ? 'ليس لديك صلاحية الطباعة'
      : "Vous n'avez pas la permission d'imprimer";
  const error = new Error(message);
  error.code = 'PRINT_DENIED';
  throw error;
}
